import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from '#src/utils/toast.ts'

const COPIED_RESET_DELAY_MS = 2000
// Stable id so rapid copies replace the visible toast instead of stacking.
const COPY_TOAST_ID = 'copied-to-clipboard'
// Keep the toast message short even for long values like full addresses.
const MAX_TOAST_VALUE_LENGTH = 50

interface UseCopyToClipboardResult {
  // True briefly after a successful copy, for swapping to a check icon.
  copied: boolean
  copy: (text: string) => Promise<void>
}

// Copies text to the clipboard and confirms with an info toast:
// `"<text>" copied to clipboard`.
export function useCopyToClipboard(): UseCopyToClipboardResult {
  const [copied, setCopied] = useState(false)
  const resetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  )

  useEffect(() => () => clearTimeout(resetTimer.current), [])

  const copy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      // Re-arm on every copy so `copied` holds for the full delay after the
      // most recent copy, not the first.
      clearTimeout(resetTimer.current)
      resetTimer.current = setTimeout(
        () => setCopied(false),
        COPIED_RESET_DELAY_MS
      )
      const value =
        text.length > MAX_TOAST_VALUE_LENGTH
          ? `${text.slice(0, MAX_TOAST_VALUE_LENGTH)}…`
          : text
      toast.info(`"${value}" copied to clipboard`, { id: COPY_TOAST_ID })
    } catch (error) {
      console.error('Failed to copy to clipboard', error)
      toast.error('Could not copy')
    }
  }, [])

  return { copied, copy }
}
